import { Controller } from "stimulus"

// Tooltip fade duration; mirrored in the inline `transition` we set in JS.
const TOOLTIP_FADE_MS = 300

// Scoped to the Payoff Plan's *initial* step only. Step transitions, pledge
// persistence, and the reveal/final views are all server-rendered Turbo Frames
// now; this controller exists solely for the slider's real-time feedback — the
// one place a server round-trip per interaction (every slider tick) would be
// jarring. It keeps the KPI strip + timeline in sync as the user drags, and
// writes the dialed-in extra into the "Reveal" form's hidden field so the
// server can render the next step from it.
//
// Custom amounts outside the precomputed steps hit /simulate. The KPI/timeline
// are the only things redrawn client-side; the donut and pledge live in the
// server-rendered reveal/final steps.
export default class extends Controller {
  static get targets() {
    return [
      "payingValue", "payingExtraWrapper", "payingExtraValue",
      "interestValue", "interestSavedWrapper", "interestSavedValue",
      "debtFreeValue", "timeSavedWrapper", "timeSavedValue", "timeSavedUnit",
      "slider", "sliderStar", "thumb", "recommendedTooltip", "maxTooltip", "customInput",
      "timelineBar", "timelineRows", "timelineEndLabel",
      "simulateError", "extraField",
    ]
  }

  static get values() {
    return {
      scenarios: Object,
      steps: Array,
      recommended: Number,
      minPayment: Number,
      totalBalance: Number,
      simulateUrl: String,
      initialExtra: Number,
    }
  }

  connect() {
    const initial = this.initialExtraValue || 0
    this._setExtra(initial)
    this._positionStar()
    this._updateThumb(Number(this.sliderTarget.value) || 0)
    this._hideTooltips()
    this._renderExtra(initial)

    // Reposition the visual thumb whenever the slider's box changes size
    // (window resize, sidebar toggles, font load). Without this the
    // pixel-anchored thumb drifts away from the track.
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(() => {
        this._positionStar()
        this._updateThumb(Number(this.sliderTarget.value) || 0)
      })
      this._resizeObserver.observe(this.sliderTarget)
    }
  }

  disconnect() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this._simulateAbort) this._simulateAbort.abort()
    if (this._tooltipTimer) clearTimeout(this._tooltipTimer)
    const tooltips = [
      this.hasRecommendedTooltipTarget ? this.recommendedTooltipTarget : null,
      this.hasMaxTooltipTarget ? this.maxTooltipTarget : null,
    ]
    tooltips.forEach((el) => { if (el && el._fadeTimer) clearTimeout(el._fadeTimer) })
  }

  // ─── Slider / custom amount ──────────────────────────────────────────────

  onSlider() {
    const snapped = this._snapToStep(Number(this.sliderTarget.value))
    this.sliderTarget.value = snapped
    if (this.hasCustomInputTarget) this.customInputTarget.value = snapped
    this._setExtra(snapped)
    this._renderExtra(snapped)
    this._flashTooltips(snapped)
    this._updateThumb(snapped)
  }

  onCustomInput() {
    const cleaned = this.customInputTarget.value.replace(/[^0-9]/g, "")
    if (this.customInputTarget.value !== cleaned) this.customInputTarget.value = cleaned
  }

  commitCustom() {
    let value = Number(this.customInputTarget.value.replace(/[^0-9]/g, "")) || 0
    // Extra is added on top of the minimum payment, so the max meaningful extra
    // is (total balance - minimum) — matching the backend's max_extra clamp.
    const maxExtra = Math.max(0, this.totalBalanceValue - this.minPaymentValue)
    value = Math.min(value, maxExtra)
    this.customInputTarget.value = value
    this._setExtra(value)

    // Peg the slider thumb at its max when the custom amount exceeds the range.
    const sliderMax = Number(this.sliderTarget.max)
    const pegged = Math.min(value, sliderMax)
    this.sliderTarget.value = pegged
    this._flashTooltips(value)
    this._updateThumb(pegged)
    this._renderExtra(value)
  }

  // The committed extra the "Reveal" form submits to the server.
  _setExtra(value) {
    if (this.hasExtraFieldTarget) this.extraFieldTarget.value = Math.round(value)
  }

  _renderExtra(extra) {
    const precomputed = this.scenarioFor(extra)
    if (precomputed) {
      this.currentScenario = precomputed
      this._applyScenario(precomputed)
    } else {
      this._simulate(extra)
    }
  }

  _snapToStep(value) {
    const steps = this.stepsValue
    return steps.reduce((best, step) =>
      Math.abs(step - value) < Math.abs(best - value) ? step : best, steps[0])
  }

  // Tooltips are exclusive to the two named positions on the track: the
  // recommended midpoint and the max. They surface only while the user is
  // changing the value and self-dismiss a moment later, so they never linger.
  _flashTooltips(value) {
    const atRecommended = value === this.recommendedValue
    const atMax = value >= Number(this.sliderTarget.max)
    this._toggleTooltip(this.hasRecommendedTooltipTarget ? this.recommendedTooltipTarget : null, atRecommended)
    this._toggleTooltip(this.hasMaxTooltipTarget ? this.maxTooltipTarget : null, atMax)

    if (this._tooltipTimer) clearTimeout(this._tooltipTimer)
    if (atRecommended || atMax) {
      this._tooltipTimer = setTimeout(() => this._hideTooltips(), 1500)
    }
  }

  _hideTooltips() {
    this._toggleTooltip(this.hasRecommendedTooltipTarget ? this.recommendedTooltipTarget : null, false)
    this._toggleTooltip(this.hasMaxTooltipTarget ? this.maxTooltipTarget : null, false)
  }

  // Fade the tooltip in/out via opacity rather than snapping `hidden`. On
  // hide we keep it displayed through the fade and only set `hidden` once the
  // transition has finished, so it leaves the a11y/layout tree after it has
  // visually disappeared (TOOLTIP_FADE_MS must match the CSS transition).
  _toggleTooltip(el, visible) {
    if (!el) return
    if (el._fadeTimer) { clearTimeout(el._fadeTimer); el._fadeTimer = null }
    el.style.transition = `opacity ${TOOLTIP_FADE_MS}ms ease`
    if (visible) {
      el.hidden = false
      // Let the browser register the un-hidden state before fading in.
      requestAnimationFrame(() => { el.style.opacity = "1" })
    } else if (!el.hidden) {
      el.style.opacity = "0"
      el._fadeTimer = setTimeout(() => { el.hidden = true; el._fadeTimer = null }, TOOLTIP_FADE_MS)
    }
  }

  // Anchor the static star marker to the exact point where the visual thumb's
  // center will land at the recommended step. Computed in pixels off the
  // slider's real rect so any track/wrapper offset is cancelled out.
  _positionStar() {
    if (!this.hasSliderStarTarget) return
    const slider = this.sliderTarget
    const offsetParent = this.sliderStarTarget.offsetParent
    if (!offsetParent) return

    const sliderRect = slider.getBoundingClientRect()
    if (sliderRect.width === 0) return

    const parentRect = offsetParent.getBoundingClientRect()
    const trackLeftOffset = sliderRect.left - parentRect.left
    const thumbWidth = this.hasThumbTarget ? (this.thumbTarget.offsetWidth || 22) : 22
    const max = Number(slider.max) || 1
    const ratio = Math.min(1, Math.max(0, this.recommendedValue / max))

    // Match the thumb's center formula at the same ratio.
    const centerPx = trackLeftOffset + (sliderRect.width - thumbWidth) * ratio + thumbWidth / 2
    this.sliderStarTarget.style.left = `${centerPx}px`
  }

  // Position the custom visual thumb along the track and morph it into a star
  // when the value lands on the recommended marker. The native input thumb is
  // kept transparent so it still receives drag/keyboard events.
  //
  // We measure the slider's actual rendered rect rather than trusting "100% of
  // the wrapper" — UA stylesheets add a small horizontal margin/padding to
  // range inputs that survives Tailwind's reset on some browsers, which leaves
  // the wrapper slightly wider than the track and the thumb visually overhangs
  // at max. Measuring the slider directly anchors the thumb to the visible bar.
  _updateThumb(value) {
    if (!this.hasThumbTarget) return
    const slider = this.sliderTarget
    const offsetParent = this.thumbTarget.offsetParent
    if (!offsetParent) return

    const sliderRect = slider.getBoundingClientRect()
    if (sliderRect.width === 0) return

    const parentRect = offsetParent.getBoundingClientRect()
    const trackLeftOffset = sliderRect.left - parentRect.left
    const thumbWidth = this.thumbTarget.offsetWidth || 22

    const max = Number(slider.max) || 1
    const ratio = Math.min(1, Math.max(0, value / max))
    const leftPx = trackLeftOffset + (sliderRect.width - thumbWidth) * ratio

    this.thumbTarget.style.left = `${leftPx}px`
    this.thumbTarget.dataset.state = (value === this.recommendedValue) ? "recommended" : "default"

    // Progress fill behind the thumb + dark recolor of the static star once
    // the thumb has reached or passed the recommended position.
    slider.style.setProperty("--payoff-fill", `${ratio * 100}%`)
    if (this.hasSliderStarTarget) {
      this.sliderStarTarget.style.color = (value >= this.recommendedValue) ? "#5A50E6" : "#C7D1FE"
    }
  }

  // ─── Scenario rendering (KPI + timeline only) ─────────────────────────────

  scenarioFor(extra) {
    return this.scenariosValue[String(Math.round(extra))] || null
  }

  _applyScenario(scenario) {
    if (!scenario) return

    this._setText(this.payingValueTargets, scenario.paying_display)
    this._setText(this.interestValueTargets, scenario.interest_display)
    this._setText(this.debtFreeValueTargets, scenario.debt_free_label)

    this._setDelta(this.payingExtraWrapperTargets, this.payingExtraValueTargets,
      scenario.paying_extra_value)
    this._setDelta(this.interestSavedWrapperTargets, this.interestSavedValueTargets,
      scenario.interest_saved_value)
    this._setTimeSaved(scenario)

    this._renderTimeline(scenario)
  }

  _setText(targets, value) {
    targets.forEach(el => { el.textContent = value })
  }

  // Toggle a delta block: write the bold number, hide the wrapper when absent.
  // Static layout (icon + label + "*" marker) is rendered server-side.
  _setDelta(wrappers, values, value) {
    const show = Boolean(value)
    wrappers.forEach(el => { el.hidden = !show })
    values.forEach(el => { el.textContent = value || "" })
  }

  _setTimeSaved(scenario) {
    const show = Boolean(scenario.time_saved_value)
    this.timeSavedWrapperTargets.forEach(el => { el.hidden = !show })
    this.timeSavedValueTargets.forEach(el => { el.textContent = scenario.time_saved_value || "" })
    this.timeSavedUnitTargets.forEach(el => { el.textContent = scenario.time_saved_unit || "" })
  }

  _renderTimeline(scenario) {
    if (this.hasTimelineEndLabelTarget) {
      this.timelineEndLabelTarget.textContent = scenario.debt_free_label
    }

    this.timelineBarTargets.forEach(bar => {
      bar.innerHTML = ""
      scenario.timeline.forEach(seg => {
        const span = document.createElement("span")
        span.style.width = `${seg.width_pct}%`
        span.style.backgroundColor = seg.color
        span.style.height = "100%"
        bar.appendChild(span)
      })
    })

    this.timelineRowsTargets.forEach(list => {
      list.innerHTML = ""
      scenario.timeline.forEach(seg => list.appendChild(this._debtRow(seg, seg.date_label)))
    })
  }

  _debtRow(seg, trailing) {
    const row = document.createElement("div")
    row.className = "tw-flex tw-items-baseline tw-gap-2 tw-text-sm"
    row.innerHTML = `
      <span style="width:8px;height:8px;border-radius:9999px;flex-shrink:0;align-self:center;background-color:${seg.color}"></span>
      <span class="tw-font-semibold tw-text-gray-500 font-inter">${this._escape(seg.name)}</span>
      <span class="tw-text-text-secondary">${this._escape(trailing)}</span>`
    return row
  }

  // ─── Networking ──────────────────────────────────────────────────────────

  _simulate(extra) {
    if (this._simulateAbort) this._simulateAbort.abort()
    const abort = new AbortController()
    this._simulateAbort = abort
    const url = `${this.simulateUrlValue}?extra=${encodeURIComponent(extra)}`
    fetch(url, { signal: abort.signal, headers: { Accept: "application/json" } })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(scenario => {
        if (abort.signal.aborted) return
        this.currentScenario = scenario
        this._applyScenario(scenario)
      })
      .catch(err => {
        if (err.name === "AbortError") return
        globalThis.Rollbar?.error("PayoffPlan: simulate failed", err)
        if (this.hasSimulateErrorTarget) this.simulateErrorTarget.hidden = false
      })
  }

  _escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
